import type { BookingRequest } from '@cocoonspace/sdk-js/v2/types/bookingRequest'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useGetBooking } from '@cocoonspace/sdk-js/domains/bookings/bookings'
import { listEvents } from '@cocoonspace/sdk-js/domains/events/events'
import { useGetSpaceSuspense } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import {
	ArrowRightIcon,
	CalendarIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	Loader2Icon,
	MapPinIcon,
	MessageCircleIcon,
	SendIcon,
	UserRoundIcon,
} from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'
import { useRouter } from '~/hooks/use-localized-navigation.hook'
import { toast } from 'sonner'

import { Duration } from '~/components/duration'
import { PageLoader } from '~/components/page-loader'
import { useSpaceAddress } from '~/domains/spaces/hooks/use-space-address.hook'
import { useIsRouterLoading } from '~/hooks/use-is-router-loading.hook'
import { formatPersonName } from '~/utils/format-person-name'
import {
	ScheduleCard,
	ScheduleCardDescription,
	ScheduleCardHeader,
	ScheduleCardTitle,
} from '../scheduling-card'
import { DeleteBookingRequestDialog } from './delete-booking-request-dialog'
import { FallbackBookingDialog } from './fallback-booking-dialog'
import { useCreateBookingFromBookingRequest } from './use-create-booking-from-booking-request'

const SeeEventButton = ({
	bookingRequest,
}: {
	bookingRequest: BookingRequest
}) => {
	const { t } = useLingui()
	const router = useRouter()

	const [isLoading, setIsLoading] = useState(false)

	const getEventAndRedirect = useCallback(async () => {
		if (!bookingRequest.bookingId) return
		setIsLoading(true)

		const events = await listEvents({ booking: bookingRequest.bookingId })
		const event = events.data.find((e) => e.status === 'validated')

		if (!event) {
			setIsLoading(false)
			return toast.error(t`Pas de réservation associee`)
		}

		Promise.resolve(router.push(`/events/${event.id}`)).finally(() => {
			setIsLoading(false)
		})
	}, [bookingRequest, router, t])

	return (
		<Button
			size='sm'
			variant='textPrimary'
			disabled={isLoading}
			onClick={getEventAndRedirect}
		>
			{isLoading && <Loader2Icon className='animate-spin' />}
			{t`Voir la réservation`} <ArrowRightIcon />
		</Button>
	)
}

const FinalizeButton = ({
	bookingRequest,
}: {
	bookingRequest: BookingRequest
}) => {
	const { t } = useLingui()
	const isRouterLoading = useIsRouterLoading()

	return (
		<Button
			size='sm'
			aria-disabled={isRouterLoading}
			className='flex-1 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50'
			asChild
		>
			<Link
				href={`/booking/${bookingRequest.bookingId}/payment?from=scheduling&bookingRequestId=${bookingRequest.id}&guest=${encodeURIComponent(bookingRequest.email)}`}
			>
				{t`Finaliser`}
			</Link>
		</Button>
	)
}

const CreateBookingButton = ({
	bookingRequest,
}: {
	bookingRequest: BookingRequest
}) => {
	const { t } = useLingui()
	const isRouterLoading = useIsRouterLoading()

	const {
		requestedSpaceQuery,
		createBookingAndRedirect,
		createBookingOptionsMutation,
		isDialogNoAvailabilitiesOpen,
		setIsDialogNoAvailabilitiesOpen,
		handleBookAlternative,
	} = useCreateBookingFromBookingRequest(bookingRequest)

	return (
		<>
			<Button
				size='sm'
				className='flex-1'
				disabled={createBookingOptionsMutation.isPending || isRouterLoading}
				onClick={() => createBookingAndRedirect()}
			>
				{t`Valider et payer`}
			</Button>

			{requestedSpaceQuery.isSuccess && (
				<FallbackBookingDialog
					open={isDialogNoAvailabilitiesOpen}
					bookingRequest={bookingRequest}
					requestedSpace={requestedSpaceQuery.data.data}
					onOpenChange={setIsDialogNoAvailabilitiesOpen}
					onBookAlternative={handleBookAlternative}
				/>
			)}
		</>
	)
}

const DateTime = ({ bookingRequest }: { bookingRequest: BookingRequest }) => (
	<ScheduleCardDescription className='flex-row items-center gap-4'>
		<div className='flex gap-2'>
			<div className='flex items-center gap-x-2 text-sm'>
				<CalendarIcon />
				<span>{dateTimeUtils(bookingRequest.day).customFormat('date')}</span>
			</div>

			<div className='flex items-center gap-1'>
				<span> {bookingRequest.start} </span>
				<ArrowRightIcon className='!size-3' />
				<span> {bookingRequest.end} </span>
			</div>
		</div>

		<div className='ml-auto flex items-center gap-x-2 text-sm'>
			<Duration duration={bookingRequest.duration} />
		</div>
	</ScheduleCardDescription>
)

const UserInfos = ({ bookingRequest }: { bookingRequest: BookingRequest }) => {
	const { first_name, last_name } = bookingRequest.additionalInfos || {}
	const fullName = formatPersonName(first_name, last_name)
	const email = bookingRequest.email || ''

	return (
		<div className='flex items-start gap-2 overflow-hidden'>
			<UserRoundIcon className='mt-0.5' />

			<div>
				<p className='truncate font-bold'>{fullName}</p>
				<p className='truncate'>{email}</p>
			</div>
		</div>
	)
}

const ActionButton = ({
	bookingRequest,
}: {
	bookingRequest: BookingRequest
}) => {
	const { data: bookingData } = useGetBooking(
		bookingRequest.bookingId as string,
	)

	const booking = bookingData?.data

	const isBalanced = booking?.status === 'balanced'
	const isPending =
		booking?.status === 'require_validation' &&
		dateTimeUtils
			.tz(booking?.created_at, 'utc')
			.isAfter(dateTimeUtils().subtract(15, 'minutes'))

	if (isBalanced) return <SeeEventButton bookingRequest={bookingRequest} />
	if (isPending) return <FinalizeButton bookingRequest={bookingRequest} />

	return <CreateBookingButton bookingRequest={bookingRequest} />
}

const Comment = ({ comment }: { comment: string }) => {
	const [expanded, setExpanded] = useState(false)
	const [isClamped, setIsClamped] = useState(false)
	const textRef = useRef<HTMLParagraphElement>(null)

	// biome-ignore lint/correctness/useExhaustiveDependencies: reclamp when comment changes
	useEffect(() => {
		const el = textRef.current

		if (el) {
			// check if text is truncated
			setIsClamped(el.scrollHeight > el.clientHeight)
		}
	}, [comment])

	return (
		<button
			type='button'
			className='flex flex-1 items-start gap-x-2 text-left'
			onClick={() => setExpanded(!expanded)}
		>
			<MessageCircleIcon className='mt-[3px]' />

			<p
				ref={textRef}
				className={cn({ 'line-clamp-1': !expanded })}
			>
				{comment}
			</p>

			{isClamped && (
				<div className='mt-[3px]'>
					{expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
				</div>
			)}
		</button>
	)
}

const SpaceInfos = ({ spaceId }: { spaceId: string }) => {
	const res = useGetSpaceSuspense(spaceId)

	const { fullAddress } = useSpaceAddress(res.data.data)

	return (
		<div className='flex items-center gap-x-2'>
			<MapPinIcon />
			<span>{fullAddress}</span>
		</div>
	)
}

export const BookingCard = ({
	bookingRequest,
}: {
	bookingRequest: BookingRequest
}) => {
	const { t } = useLingui()

	const comment = bookingRequest.additionalInfos?.comment

	const hasSpace =
		bookingRequest.requestedLocation.type === 'cocoon' &&
		!!bookingRequest.requestedLocation.space_id &&
		bookingRequest.requestedLocation.space_id !== 'remote'

	const createdAtDate = dateTimeUtils(bookingRequest.createdAt).format(
		'DD/MM/YYYY',
	)

	const createdAtTime = dateTimeUtils(bookingRequest.createdAt).format('HH:mm')

	return (
		<ScheduleCard className='gap-0 divide-y p-0'>
			<div className='p-4'>
				<ScheduleCardHeader>
					<ScheduleCardTitle className='flex flex-row items-center gap-2'>
						{bookingRequest.title}
					</ScheduleCardTitle>
				</ScheduleCardHeader>

				<DateTime bookingRequest={bookingRequest} />
			</div>

			<div className='flex flex-1 flex-col items-start justify-start gap-2 p-4 text-sm'>
				<UserInfos bookingRequest={bookingRequest} />
				{comment && <Comment comment={comment} />}

				{hasSpace && (
					<Suspense fallback={<PageLoader className='mx-auto' />}>
						<SpaceInfos
							spaceId={bookingRequest.requestedLocation.space_id as string}
						/>
					</Suspense>
				)}

				{bookingRequest.createdAt && (
					<div className='flex items-center gap-x-2'>
						<SendIcon />
						<span>{t`Réponse envoyée le ${createdAtDate} à ${createdAtTime}`}</span>
					</div>
				)}
			</div>

			<div className='flex justify-center gap-4 p-4'>
				<DeleteBookingRequestDialog bookingRequest={bookingRequest} />
				<ActionButton bookingRequest={bookingRequest} />
			</div>
		</ScheduleCard>
	)
}
